/* eslint-disable max-len */
import { shadeSafe, tintSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { Line } from '../../../platform/geom/Line';
import { toRadians } from '../../../platform/geom/Math';
import { Matrix } from '../../../platform/geom/Matrix';
import { Path } from '../../../platform/geom/Path';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { ShapeKind, ShapeStyle } from './ShapeEnums';

export interface ShapeSettings {
    readonly color: string;
    readonly cornerRadius: number;
    readonly stroke: number;
    readonly sides: number;
    readonly style: ShapeStyle;
    readonly width: number;
    readonly height: number;
    readonly tail?: Point;
    readonly dash?: number[];
    readonly custom?: any;
}

export type ShapePath = { d: Path } & Record<string, any>;
export type ShapeData = {
    bounds: Rect,
    paths: ShapePath[],
};

export type ShapeAlgorithm = (cfg: ShapeSettings) => ShapeData;

const typicalStrokeWidth = ({ stroke, style }: ShapeSettings) => {
    if (style === ShapeStyle.fill) return 0;
    if (style === ShapeStyle.stroke) return stroke;
    return stroke;
};

const computeColors = ({ color, style }: ShapeSettings) => {
    switch (style) {
        case ShapeStyle.outline:
        case ShapeStyle.dash:
            const stroke = color;
            const fill = tintSafe(.75, color);
            return (Colors.distance(stroke, fill) >= 20)
                ? { stroke, fill }
                : { stroke: shadeSafe(.1, color), fill };
        case ShapeStyle.stroke:
            return { fill: null, stroke: color };
        case ShapeStyle.fillWhite:
            return { fill: 'white', stroke: color };
        default:
            return { fill: color, stroke: null };
    }
};

const createSinglePathShapeData = (cfg: ShapeSettings, path: Path) => {

    const d = path;
    const { stroke, fill } = computeColors(cfg);
    const strokeWidth = typicalStrokeWidth(cfg);
    const strokeDasharray = (
        (cfg.style === ShapeStyle.dash && !!cfg.dash ? cfg.dash.join(' ') : null) ??
        (cfg.style === ShapeStyle.dash ? `${4 * strokeWidth}` : null) ??
        ''
    );

    const paths = [] as ShapePath[];

    if (fill || !strokeWidth) {
        paths.push({ d, fill: fill ?? 'none', stroke: 'none' });
    }
    if (strokeWidth) {
        paths.push({ d, fill: 'none', stroke, strokeWidth, strokeDasharray, strokeLinejoin: 'round', });
    }

    return {
        paths,
        bounds: path.bounds(strokeWidth),
    };
};

// Notes on the ShapeAlgorithms:
//
// Some work using a unit base that is scaled up using the width/height while others work from a pre-scaled base.  Sometimes its easier to
// use one vs the other.  For example, the paragramAlgorithm needs to create an indent/offset that is derived from the current width and
// height which would need to be scaled down.

const rectangleAlgorithm = (cfg: ShapeSettings) => {
    const { cornerRadius, width, height } = cfg;

    const base = [
        new Point(0, 0),
        new Point(1, 0),
        new Point(1, 1),
        new Point(0, 1),
    ];

    const strokeWidth = typicalStrokeWidth(cfg);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    // const rounding = Math.min(width / 2, height / 2) * (cornerRadius / 100);
    const path = Polyline.from(mt.apply(base), true).toRoundedPath('cubic', cornerRadius);

    return createSinglePathShapeData(cfg, path);
};

const ovalAlgorithm = (cfg: ShapeSettings) => {
    const { width, height, } = cfg;

    const base = [
        new Point(0, 0),
        new Point(1, 0),
        new Point(1, 1),
        new Point(0, 1),
    ];

    const strokeWidth = typicalStrokeWidth(cfg);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    // const fillColor = style === ShapeStyle.fill.value ? color : tintSafe(.75, color);
    // const strokeColor = style === ShapeStyle.fill.value ? 'none' : color;
    // const strokeDasharray = style === ShapeStyle.dash ? 4 * strokeWidth : '';

    const a = Rect.fromPoints(mt.apply(base));
    const c = a.center();
    const r = a.size().divide(2);

    const path = Path.ellipse(c.x, c.y, r.x, r.y);
    return createSinglePathShapeData(cfg, path);
    // const items: ShapeData = [
    //     {
    //         type: 'ellipse', cx: c.x, cy: c.y, rx: r.x, ry: r.y, fill: fillColor,
    //         stroke: strokeColor, strokeWidth, strokeDasharray, strokeLinejoin: 'round'
    //     },
    // ];

    // return items;
};

const paragramAlgorithm = (cfg: ShapeSettings) => {
    const { cornerRadius, width, height } = cfg;

    // Parallelogram is a rectangle with an offset top left and bottom right.  We offset by an amount that is variable depending on width or
    // height but that results in a 12 degree angle in the bottom left.  To compute, use trig to work out the length of the offset (opposite
    // side length) and multiply by the ratio of height to width to cater for asymetrical rectangles.
    const t = toRadians(12);
    const o = Math.atan(t) * (height / width);

    // Generate the unit base using the computed offset.
    const base = [
        new Point(0 + o, 0),
        new Point(1, 0),
        new Point(1 - o, 1),
        new Point(0, 1),
    ];

    const strokeWidth = typicalStrokeWidth(cfg);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    // Generate path.
    // const rounding = Math.min(width, height) * (cornerRadius / 100);
    const path = Polyline.from(mt.apply(base), true).toRoundedPath('cubic', cornerRadius);

    return createSinglePathShapeData(cfg, path);
};

const trapezoidAlgorithm = (cfg: ShapeSettings) => {
    const { cornerRadius, width, height } = cfg;

    // Parallelogram is a rectangle with an offset top left and bottom right.  We offset by an amount that is variable depending on width or
    // height but that results in a 12 degree angle in the bottom left.  To compute, use trig to work out the length of the offset (opposite
    // side length) and multiply by the ratio of height to width to cater for asymetrical rectangles.
    const t = toRadians(12);
    const o = Math.atan(t) * (height / width);

    // Generate the unit base using the computed offset.
    const base = [
        new Point(0 + o, 0),
        new Point(1 - o, 0),
        new Point(1, 1),
        new Point(0, 1),
    ];

    const strokeWidth = typicalStrokeWidth(cfg);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    // Generate path.
    // const rounding = Math.min(width, height) * (cornerRadius / 100);
    const path = Polyline.from(mt.apply(base), true).toRoundedPath('cubic', cornerRadius);

    return createSinglePathShapeData(cfg, path);
};

const createAlgorithmFromBasePath = (pathFactory: (cfg: ShapeSettings) => Path) => (cfg: ShapeSettings) => {
    const { cornerRadius, width, height } = cfg;
    const strokeWidth = typicalStrokeWidth(cfg);

    // Create the base path
    const base = pathFactory(cfg);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    // Generate path.
    // const rounding = Math.min(width, height) * (cornerRadius / 100);
    const path = mt.apply(base).toRounded('cubic', cornerRadius).toClosed();

    return createSinglePathShapeData(cfg, path);
};

const quoteAlgorithm = createAlgorithmFromBasePath(cfg => {

    const d = 'M 10 5 C 10 7.7615 7.7614 10 5 10 C 2.2386 10 0 7.7615 0 5 C 0 2.2387 2.2386 0 5 0 L 6 0 L 10 0 L 10 4 L 10 5 Z';
    const path = Path.parse(d);

    // const path = new Path([
    //     ['M', new Point(10, 4.99991)],
    //     ['C', new Point(10, 7.76133), new Point(7.76142, 9.99991), new Point(5, 9.99991)],
    //     ['C', new Point(2.23858, 9.99991), new Point(0, 7.76133), new Point(0, 4.99991)],
    //     ['C', new Point(0, 2.23848), new Point(2.23858, -9.34601e-05), new Point(5, -9.34601e-05)],
    //     ['C', new Point(7, -0.000244141), new Point(8, -9.34601e-05), new Point(10, -0.000244141)],
    //     ['C', new Point(10, 2), new Point(10, 3), new Point(10, 4.99991)],
    //     ['Z']
    // ]);

    return path.scale(.1);
});

const intrudeBannerAlgorithm = createAlgorithmFromBasePath(cfg => {
    const { width, height } = cfg;

    // _________
    // \       /
    // /_______\
    //

    return new Path([
        ['M', new Point(0, 0),],
        ['L', new Point(1, 0),],
        ['L', new Point(1 - (height / (width * 2)), .5),],
        ['L', new Point(1, 1),],
        ['L', new Point(0, 1),],
        ['L', new Point(0 + (height / (width * 2)), .5),],
    ]);
});

const intrudeRightBannerAlgorithm = createAlgorithmFromBasePath(cfg => {
    const { width, height } = cfg;

    // _________
    // \       |
    // /_______|
    //

    return new Path([
        ['M', new Point(0, 0),],
        ['L', new Point(1, 0),],
        ['L', new Point(1, 1),],
        ['L', new Point(0, 1),],
        ['L', new Point(0 + (height / (width * 2)), .5),],
    ]);
});

const intrudeLeftBannerAlgorithm = createAlgorithmFromBasePath(cfg => {
    const { width, height } = cfg;

    // _________
    // |       /
    // |_______\
    //

    return new Path([
        ['M', new Point(0, 0),],
        ['L', new Point(1, 0),],
        ['L', new Point(1 - (height / (width * 2)), .5),],
        ['L', new Point(1, 1),],
        ['L', new Point(0, 1),],
    ]);
});

const extrudeBannerAlgorithm = createAlgorithmFromBasePath(cfg => {
    const { width, height } = cfg;

    //  _______
    // /       \
    // \_______/
    //

    return new Path([
        ['M', new Point(0 + (height / (width * 2)), 0)],
        ['L', new Point(1 - (height / (width * 2)), 0)],
        ['L', new Point(1, .5)],
        ['L', new Point(1 - (height / (width * 2)), 1)],
        ['L', new Point(0 + (height / (width * 2)), 1)],
        ['L', new Point(0, .5)],
    ]);
});

const extrudeRightBannerAlgorithm = createAlgorithmFromBasePath(cfg => {
    const { width, height } = cfg;

    //  _______
    // |       \
    // |_______/
    //

    return new Path([
        ['M', new Point(0, 0)],
        ['L', new Point(1 - (height / (width * 2)), 0)],
        ['L', new Point(1, .5)],
        ['L', new Point(1 - (height / (width * 2)), 1)],
        ['L', new Point(0, 1)],
    ]);
});

const extrudeLeftBannerAlgorithm = createAlgorithmFromBasePath(cfg => {
    const { width, height } = cfg;

    //  _______
    // /       |
    // \_______|
    //

    return new Path([
        ['M', new Point(0 + (height / (width * 2)), 0)],
        ['L', new Point(1, 0)],
        ['L', new Point(1, 1)],
        ['L', new Point(0 + (height / (width * 2)), 1)],
        ['L', new Point(0, .5)],
    ]);
});

const createPolygonAlgorithm = (sides: number) => (cfg: ShapeSettings) => {
    const { cornerRadius, width, height } = cfg;

    // Use ellipse to compute locations of points that make up the polygon.  These will not "fit" into the bounding box due to just geom
    // things so once we have the points, compute the gap on both X and Y and use this to create a scale factor that we can use to resize
    // the points so they fit.  We will need to first move the points so that the are flush left and top then scale from top left using the
    // computed factor.  This will work for any shape.

    const angle = (Math.PI * 2) / sides;
    const base = [] as Point[];

    // Generate the unit base by rotating around the ellipse.
    for (let i = 0; i < sides; i++) {
        const pt = new Point(
            0.5 * Math.cos((angle * i) - Math.PI / 2),
            0.5 * Math.sin((angle * i) - Math.PI / 2),
        );
        base.push(pt);
    }

    // Points generated will exist on border of ellipse which will not fit directly into the specified box, so compute the scale and
    // translation adjustments required to make the shape fit and apply them as part of the scale up process.

    const y1 = Math.min(...base.map(x => x.y));
    const y2 = Math.max(...base.map(x => x.y));
    const ys = 1 / (y2 - y1);

    const x1 = Math.min(...base.map(x => x.x));
    const x2 = Math.max(...base.map(x => x.x));
    const xs = 1 / (x2 - x1);

    const strokeWidth = typicalStrokeWidth(cfg);

    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        .scale(xs, ys) // Scale to remove shape cap
        .translate(0.5, 0.5) // Set origin as center
        .translate(-0.5 - x1, -0.5 - y1); // Offset based on shape gap

    // const rounding = Math.min(width, height) * (cornerRadius / 100);
    const path = Polyline.from(mt.apply(base), true).toRoundedPath('cubic', cornerRadius);

    return createSinglePathShapeData(cfg, path);
};

const triangleAlgorithm = createPolygonAlgorithm(3);
const rhombusAlgorithm = createPolygonAlgorithm(4);
const pentagonAlgorithm = createPolygonAlgorithm(5);
const hexagonAlgorithm = createPolygonAlgorithm(6);
const octagonAlgorithm = createPolygonAlgorithm(8);

const createStarAlgorithm = (radius: number) => (cfg: ShapeSettings) => {
    const { cornerRadius, width, height, sides } = cfg;

    // Use ellipse to compute locations of points that make up the polygon.  These will not "fit" into the bounding box due to just geom
    // things so once we have the points, compute the gap on both X and Y and use this to create a scale factor that we can use to resize
    // the points so they fit.  We will need to first move the points so that the are flush left and top then scale from top left using the
    // computed factor.  This will work for any shape.

    const angle = (Math.PI * 2) / sides;
    let base = [] as Point[];

    // Generate the unit base by rotating around the ellipse.
    for (let i = 0; i < sides; i++) {
        const pt = new Point(
            0.5 * Math.cos((angle * i) - Math.PI / 2),
            0.5 * Math.sin((angle * i) - Math.PI / 2),
        );
        base.push(pt);
    }

    // To create star, take the segment between each point and the next and add a point along that segment at a distance of 0.5 from the center.
    const origin = Point.average(base);
    const inners = base.map((_, i) => {
        const ln = new Line(base[i], base[i + 1] ?? base[0]);
        const v = Point.vector(ln.mid, origin).multiply(radius);
        return ln.mid.add(v);
    });

    // Interleave base and points
    base = base.flatMap((x, i) => [x, inners[i]]);

    // Points generated will exist on border of ellipse which will not fit directly into the specified box, so compute the scale and
    // translation adjustments required to make the shape fit and apply them as part of the scale up process.

    const y1 = Math.min(...base.map(x => x.y));
    const y2 = Math.max(...base.map(x => x.y));
    const ys = 1 / (y2 - y1);

    const x1 = Math.min(...base.map(x => x.x));
    const x2 = Math.max(...base.map(x => x.x));
    const xs = 1 / (x2 - x1);

    const strokeWidth = typicalStrokeWidth(cfg);

    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        .scale(xs, ys) // Scale to remove shape cap
        .translate(0.5, 0.5) // Set origin as center
        .translate(-0.5 - x1, -0.5 - y1); // Offset based on shape gap

    // const rounding = Math.min(width, height) * (cornerRadius / 100);
    const path = Polyline.from(mt.apply(base), true).toRoundedPath('cubic', cornerRadius);

    return createSinglePathShapeData(cfg, path);
};

const starAlgorithm = createStarAlgorithm(.45);
const badgeAlgorithm = createStarAlgorithm(.2);

const rightangleAlgorithm = (cfg: ShapeSettings) => {
    const { cornerRadius, width, height } = cfg;

    const base = [
        new Point(0, 0),
        new Point(1, 1),
        new Point(0, 1),
    ];

    const strokeWidth = typicalStrokeWidth(cfg);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    const rounding = Math.min(width / 2, height / 2) * (cornerRadius / 100);
    const path = Polyline.from(mt.apply(base), true).toRoundedPath('cubic', rounding);

    return createSinglePathShapeData(cfg, path);
};

const lineArrowAlgorithm = (cfg: ShapeSettings) => {
    const { color, stroke, style, width, height } = cfg;

    const size = Math.min(width, height);

    const strokeColor = color;
    const strokeWidth = Math.max(2, Math.round((size / 4) * (stroke / 100)));

    const headBase1 = [new Point(1, .5), new Point(.4, 0)];
    const headBase2 = [new Point(1, .5), new Point(.4, 1)];
    const bodyBase = [new Point(0, .5), new Point(1, .5)];

    // Scale up and apply the stroke offset.
    const headMat = Matrix
        .translate(width - size, 0) // Move head to end of shape
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((size - strokeWidth) / size, (size - strokeWidth) / size) // Reduce size to fit stroke
        .scale(size, size) // Scale up to desired size
        ;

    const bodyMat = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    const head1 = Polyline.from(headMat.apply(headBase1)).toPath(false);
    const head2 = Polyline.from(headMat.apply(headBase2)).toPath(false);
    const body = Polyline.from(bodyMat.apply(bodyBase)).toPath(false);
    const arrow = head1.concat(head2, body);
    const strokeDasharray = style === ShapeStyle.dash ? 4 * strokeWidth : '';

    return {
        bounds: new Rect(0, 0, width, height),
        paths: [{
            d: arrow,
            fill: 'none',
            stroke: strokeColor,
            strokeWidth,
            strokeDasharray,
            strokeLinejoin: 'round',
            strokeLinecap: 'round'
        }]
    };
};

const coolArrowAlgorithm = (cfg: ShapeSettings) => {
    const { cornerRadius, width, height } = cfg;

    const size = Math.min(width, height);
    const radius = Math.min(size * (cornerRadius / 100));
    const strokeWidth = typicalStrokeWidth(cfg);

    const headBase = [
        new Point(1, .5),
        new Point(.4, 0),
        new Point(.5, .35),
        new Point(.5, .65),
        new Point(.4, 1),
    ];

    const bodyBase = [
        // new Point(.3 * (height / width), .4),
        new Point(0, .3),
        new Point(0, .7),
        // new Point(.30 * (height / width), .6),
    ];

    // Scale up and apply the stroke offset.
    const headMat = Matrix
        .translate(width - size, 0) // Move head to end of shape
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((size - strokeWidth) / size, (size - strokeWidth) / size) // Reduce size to fit stroke
        .scale(size, size); // Scale up to desired size

    // Scale up and apply the stroke offset.
    const bodyMat = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    const head = headMat.apply(headBase);
    const body = bodyMat.apply(bodyBase);

    const arrow = head.slice();
    arrow.splice(3, 0, ...body);

    const rounding = [radius, 0, radius, radius, 0, radius, radius, radius];
    const path = Polyline.from(arrow, true).toRoundedPath('cubic', rounding);

    return createSinglePathShapeData(cfg, path);
};

const fatArrowAlgorithm = (cfg: ShapeSettings) => {
    const { cornerRadius, width, height } = cfg;

    const size = Math.min(width, height);
    const radius = Math.min(size * (cornerRadius / 100));
    const strokeWidth = typicalStrokeWidth(cfg);

    const headBase = [
        new Point(.4, 0),
        new Point(.4, .35),
        new Point(.4, .65),
        new Point(.4, 1),
        new Point(1, .5),
    ];

    const bodyBase = [
        // new Point(.25 * (height / width), .35),
        new Point(0, .35),
        new Point(0, .65),
        // new Point(.25 * (height / width), .65),
    ];

    // Scale up and apply the stroke offset.
    const headMat = Matrix
        .translate(width - size, 0) // Move head to end of shape
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((size - strokeWidth) / size, (size - strokeWidth) / size) // Reduce size to fit stroke
        .scale(size, size); // Scale up to desired size

    // Scale up and apply the stroke offset.
    const bodyMat = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    const head = headMat.apply(headBase);
    const body = bodyMat.apply(bodyBase);

    const arrow = head.slice();
    arrow.splice(2, 0, ...body);

    const rounding = [0, radius, radius, 0, radius, radius, radius];
    const path = Polyline.from(arrow, true).toRoundedPath('cubic', rounding);

    return createSinglePathShapeData(cfg, path);
};

const bannerArrowAlgorithm = createAlgorithmFromBasePath(cfg => {
    const { width, height } = cfg;

    // ________
    // \       \
    // /_______/
    //

    return new Path([
        ['M', new Point(0, 0)],
        ['L', new Point(1 - (height / (width * 2)), 0)],
        ['L', new Point(1, .5)],
        ['L', new Point(1 - (height / (width * 2)), 1)],
        ['L', new Point(0, 1)],
        ['L', new Point(0 + (height / (width * 2)), .5),],
    ]);
});

const heartAlgorithm = (cfg: ShapeSettings) => {
    const { width, height } = cfg;

    const strokeWidth = typicalStrokeWidth(cfg);

    const base = new Path([
        ['M', new Point(.27, 0)],
        ['C', new Point(.12, 0), new Point(0, .15), new Point(0, .30)],
        ['C', new Point(0, .53), new Point(.35, .85), new Point(.50, 1)],
        ['C', new Point(.65, .85), new Point(1, .53), new Point(1, .30)],
        ['C', new Point(1, .15), new Point(.88, 0), new Point(.73, 0)],
        ['C', new Point(.58, 0), new Point(.50, .15), new Point(.50, .30)],
        ['C', new Point(.50, .15), new Point(.42, 0), new Point(.27, 0)],
        ['Z']
    ]);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;


    const path = mt.apply(base);// Polyline.from(mt.apply(base)).toPath();

    return createSinglePathShapeData(cfg, path);
};

const braceAlgorithm = (cfg: ShapeSettings) => {
    const { color, stroke, style, width, height } = cfg;

    const size = Math.min(width, height);
    const strokeWidth = Math.max(2, Math.round((size / 4) * (stroke / 100)));
    // const strokeWidth = typicalStrokeWidth(cfg);

    const base = Path.parse('M10 0C6 0.5 5.5 1.5 5 2.5C4.5 3.5 4 4.5 0 5C4 5.5 4.5 6.5 5 7.5C5.5 8.5 6 9.5 10 10').scale(.1);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    const path = mt.apply(base);// Polyline.from(mt.apply(base)).toPath();
    const strokeDasharray = style === ShapeStyle.dash ? 4 * strokeWidth : '';

    return {
        bounds: path.bounds(),
        paths: [{
            d: path,
            fill: 'none',
            stroke: color,
            strokeWidth,
            strokeDasharray,
            strokeLinejoin: 'round',
            strokeLinecap: 'round'
        }]
    };

    // return createSinglePathShapeData(cfg, path);
};

const squareBraceAlgorithm = (cfg: ShapeSettings) => {
    const { color, stroke, style, width, height } = cfg;

    const size = Math.min(width, height);
    const strokeWidth = Math.max(2, Math.round((size / 4) * (stroke / 100)));
    // const strokeWidth = typicalStrokeWidth(cfg);

    const base = Path.parse('M 0 10 L 0 0 L 10 0 L 10 10').scale(.1);

    // Scale up and apply the stroke offset.
    const mt = Matrix
        .translate(strokeWidth / 2, strokeWidth / 2) // Offset to move inside container
        .scale((width - strokeWidth) / width, (height - strokeWidth) / height) // Reduce size to fit stroke
        .scale(width, height) // Scale up to desired size
        ;

    const path = mt.apply(base);// Polyline.from(mt.apply(base)).toPath();
    const strokeDasharray = style === ShapeStyle.dash ? 4 * strokeWidth : '';

    return {
        bounds: path.bounds(),
        paths: [{
            d: path,
            fill: 'none',
            stroke: color,
            strokeWidth,
            strokeDasharray,
            strokeLinejoin: 'round',
            strokeLinecap: 'round'
        }]
    };
};

const customAlgorithm = createAlgorithmFromBasePath((cfg: ShapeSettings) => {
    const { custom } = cfg;

    const base = new Point(custom.width, custom.height);
    const path = Path.parse(custom.paths[0]);

    const mt = Matrix.scale(1 / base.x, 1 / base.y);
    const scaled = path.transform(mt);

    return scaled;
});

const mappings = {
    [ShapeKind.rectangle]: rectangleAlgorithm,
    [ShapeKind.oval]: ovalAlgorithm,
    [ShapeKind.triangle]: triangleAlgorithm,
    [ShapeKind.rightangle]: rightangleAlgorithm,
    [ShapeKind.rhombus]: rhombusAlgorithm,
    [ShapeKind.pentagon]: pentagonAlgorithm,
    [ShapeKind.hexagon]: hexagonAlgorithm,
    [ShapeKind.octagon]: octagonAlgorithm,
    [ShapeKind.paragram]: paragramAlgorithm,
    [ShapeKind.trapezoid]: trapezoidAlgorithm,
    [ShapeKind.quote]: quoteAlgorithm,
    [ShapeKind.heart]: heartAlgorithm,
    [ShapeKind.arrow1]: lineArrowAlgorithm,
    [ShapeKind.arrow2]: fatArrowAlgorithm,
    [ShapeKind.arrow3]: coolArrowAlgorithm,
    [ShapeKind.arrow4]: bannerArrowAlgorithm,
    [ShapeKind.star]: starAlgorithm,
    [ShapeKind.badge]: badgeAlgorithm,
    [ShapeKind.banner1]: intrudeBannerAlgorithm,
    [ShapeKind.banner2]: extrudeBannerAlgorithm,
    [ShapeKind.banner3]: intrudeLeftBannerAlgorithm,
    [ShapeKind.banner4]: extrudeLeftBannerAlgorithm,
    [ShapeKind.banner5]: intrudeRightBannerAlgorithm,
    [ShapeKind.banner6]: extrudeRightBannerAlgorithm,
    [ShapeKind.brace]: braceAlgorithm,
    [ShapeKind.squareBrace]: squareBraceAlgorithm,
};

export const ShapeAlgorithm = Object.freeze({
    for(kind: ShapeKind): ShapeAlgorithm {

        const alg = mappings[kind] ?? rectangleAlgorithm;

        return (cfg: ShapeSettings) => {
            try {
                return alg(cfg);
            }
            catch (e) {
                return rectangleAlgorithm(cfg);
            }
        };
    },
});
